import useChats from "contexts/Chat/useChats";
import {FC, useMemo} from "react";
import {Outlet, useLocation, useNavigate, useParams} from "react-router";
import Chat from "./Chat";
import useAuthContext from "contexts/Auth/useAuthContext";
import ShortProfile from "./ShortProfile";
import useHideSupportChat from "hooks/useHideSupportChat";
import {DefaultProfileSrc} from "assets/images";
import ProfileImage from "components/ProfileImage";

export const ChatPage: FC = () => {
  const auth = useAuthContext()!;
  const chatId = useParams().chatId!;
  const {threads} = useChats();
  const otherMembers = useMemo(
    () => threads.find((thread) => thread.id === chatId)?.members.filter((id) => id !== auth.uid),
    [chatId, threads, auth.uid],
  );

  return (
    <>
      <Chat chatId={chatId} />
      {otherMembers?.length === 1 && <ShortProfile user={otherMembers[0]} />}
    </>
  );
};

const ChatsPage: FC = () => {
  useHideSupportChat();
  const {threads, usersLastActiveAt} = useChats();
  const auth = useAuthContext();

  const chatId = useLocation().pathname.split("/")[2];
  const navigate = useNavigate();

  return (
    <div className="w-full h-full overflow-hidden flex bg-brand-surface p-5 gap-4">
      <div className="w-72 shrink-0 flex flex-col overflow-y-auto rounded-xl bg-white border border-brand-border">
        {threads.map((thread) => {
          const {name, image} = thread.otherProfiles[0];
          const notMe = thread.members.find((id) => id !== auth?.uid)!;
          const lastMessage = thread.latestMessage?.content;
          const hasUnread = thread.unread > 0;
          const isLastFile = !!thread.latestMessage?.file;
          const lastMessageText = isLastFile ? "File" : lastMessage || "Start a conversation";
          const isActive = usersLastActiveAt[notMe]
            ? new Date(usersLastActiveAt[notMe]).getTime() > Date.now() - 5 * 60 * 1000
            : undefined;

          return (
            <div
              key={thread.id}
              onClick={() => navigate(`/chat/${thread.id}`)}
              className={
                [
                  "flex items-center border-b border-brand-border",
                  "gap-3.5 p-4 transition-colors cursor-pointer",
                  chatId === thread.id ? "bg-brand-orange-bg" : "hover:bg-brand-orange-bg",
                  hasUnread && "font-semibold",
                ].asClass
              }
            >
              <div className="relative w-12 h-12 shrink-0">
                <ProfileImage
                  src={image || DefaultProfileSrc}
                  size={48}
                  className="w-12 h-12 rounded-full object-cover"
                />
                {isActive !== undefined && (
                  <div
                    className={
                      [
                        "absolute w-3 h-3 rounded-full bottom-0 right-0 border-2 border-white",
                        isActive ? "bg-brand-mint" : "bg-red-400",
                      ].asClass
                    }
                  />
                )}
              </div>
              <div className="flex flex-col grow min-w-0">
                <span className="font-medium text-sm text-brand-navy truncate">{name}</span>
                <span className="text-xs text-brand-muted truncate">{lastMessageText}</span>
              </div>
              {hasUnread && (
                <span className="w-5 h-5 rounded-full bg-brand-amber text-xs flex items-center justify-center text-white font-bold flex-shrink-0">
                  {thread.unread}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
};

export default ChatsPage;
